// EditableTable.tsx
import React, { useState, useEffect, useCallback, SyntheticEvent } from 'react';
import './edit-table.css';

import Tooltip from '@plasma/ui.display.tooltip';
import Notification from '@plasma/ui.feedback.notification';
import PopConfirm from '@plasma/ui.feedback.pop-confirm';
import Button from '@plasma/ui.general.button';
import Input from '@plasma/ui.input.input';
import SearchInput from '@plasma/ui.input.search-input';
import Select from '@plasma/ui.input.select';
import Switch from '@plasma/ui.input.switch';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import Hamburger from '../hamburger';
import LoadingComponent from '../Loding-component/LoadingComponent';
import LoadingComponentScalable from '../Loding-component/LoadingComponentMini';
import generateColors from './edit-table-coloursGenerator';
//import generateRandomColor from '../../types/generateRandomColor';
import * as styled from './edit-table.style';
import themes from './themes';

export interface tableColumnsType {
  Header: string;
  accessor: string;
  editType: 'inputField' | 'Combobox' | 'Number' | 'Boolean';
  options?: Array<{ display: string; value: string }>;
  linkType?: boolean; //if field to be showed as link type
  linkExceptions?: string[]; //any string data that shuold not be hyperlink,
}

export interface EditableTableProps {
  tableData: Array<{ [key: string]: any }>;
  tableColumns: tableColumnsType[];
  handleSave?: (newData: Array<{ [key: string]: any }> | { [key: string]: any }, returnType?: string) => void;
  hiddenColumns?: string[];
  stillHiddenColumns?: string[];
  showActions_ext?: boolean;
  allowSelection?: boolean;
  rowIndexSelected?: number;
  handleSelection?: (
    selectedRowIndex: number | null | undefined,
    selectedRowData: { [key: string]: any } | null | undefined,
    event: React.SyntheticEvent | null | undefined,
    columnName?: string | null | undefined,
  ) => void;
  saveReturnType?: 'complete' | 'row';
  onlyAddableFields?: string[];
  mandatoryFields?: string[];
  Table_Header?: string;
  selectInputReturnType?: 'display' | 'value';
  groupByColours?: boolean;
  ColourColoumnName?: string;
  SubGroupColumnName?: string;
  allowInsertInitialBlankRow?: boolean;
  defaultValuesColumns?: Array<[string]>;
  clearTheSelection: boolean;
  expandableColumns?: string[];
  showSerialNumbers?: boolean;
  showLoadingOnSelection?: boolean;
  loadingSpinnerVisible?: boolean;
  showOnlyEditIcon?: boolean;
  showOnlyDeleteIcon?: boolean;
  ColorOptions?: {
    mainGroupColumnName: string;
    subgroupColumnName: string;
    seed?: number;
    baseColor?: string;
    transparency?: number;
    thickBorders?: boolean;
    useTheColorOptions?: boolean;
  };
  ButtonsVisible?: {
    Edit?: boolean;
    MoveUp?: boolean;
    InsertUp?: boolean;
    InsertDown?: boolean;
    MoveDown?: boolean;
    Delete?: boolean;
  };
  searchBarOptions?: {
    showSearch: boolean;
    visible: boolean;
    non_searchable_columns: string[];
    toggleSearch: (visible: boolean) => void;
    handleSearch: (searchTerm: string, columnName: string) => void;
  };
}

type Props = EditableTableProps & ThemeOverride;

const EditableTable: React.FC<Props> = ({
  tableData, //data to display on the table
  tableColumns, //column names
  handleSave, //save handler
  saveReturnType = 'complete', //used with handle save, 'complete' returns whole table, 'row' returns only changeing row. retrun type also inclued the type of operation,
  hiddenColumns, //columns that should not be shown. but these get shown in the Edit and Insert modes.
  stillHiddenColumns = [], //Columns that are visible even in edit in insert mode.
  showActions_ext, //whether to show the CRUD action buttons on hover
  allowSelection = false, //allow selection of row,
  rowIndexSelected = -1, // set row selection from outside.
  handleSelection, //Selection handler, allowSelection should be true., returns Row and Index
  onlyAddableFields = [], //Key fiedls, that are allowed to insert but not change after
  mandatoryFields = [], //Have tobe filled while inserting. on unseuccessful save it throws notification.
  Table_Header = '', //Header to show on the Table Heading.
  selectInputReturnType, //Special function for Select, to control return type, return the 'display' Data Or the 'value'.
  groupByColours = false, //Group the fields by color.
  allowInsertInitialBlankRow = false, //if this is set to true, and the table loads empty then it shows an initial blank row, otherwise a the animated loading icon
  defaultValuesColumns = [], //when new rows are inserted the these columns values will be copied from index where the insert was clicked
  clearTheSelection = false, //if this is changed to true at runtime then the selectio is cleared, The external variable should be a useState variable.
  expandableColumns = [], //these columns can expand to show a list.
  showSerialNumbers = true, //show serialnumebr column or not
  showLoadingOnSelection = false, //if this is set to true, a loding animation can be shown on the selected row from a state variable from outside.
  loadingSpinnerVisible = true, //If this is set to true, loading spinner will be shown while the datas are loading in the grid.
  showOnlyEditIcon = false, //If it is set to true, only the edit option & icon will be visible in the grid
  showOnlyDeleteIcon = false, //If it is set to true, only the delete option & icon will be visible in the grid
  ColorOptions = {
    //The type of colours to use.
    mainGroupColumnName: '',
    subgroupColumnName: '',
    seed: 0,
    baseColor: '#FFFFFFFF',
    transparency: 0.7,
    thickBorders: true,
    useTheColorOptions: false,
  },
  ButtonsVisible = {
    //The buttons to make visible, default all visible.
    Edit: true,
    MoveUp: true,
    InsertUp: true,
    InsertDown: true,
    MoveDown: true,
    Delete: true,
  },
  searchBarOptions = {
    showSearch: false,
    visible: false,
    non_searchable_columns: [],
    toggleSearch: () => {},
    handleSearch: () => {},
  },
  themeMode,
}) => {
  const [hamBurgerOpen, sethamburgerOpen] = useState(false);
  const [editableData, setEditableData] = useState<Array<{ [key: string]: any }>>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRowIndex, setEditedRowIndex] = useState(-1);
  const [isInserting, setIsInserting] = useState(false); // New state for tracking insert
  const [showActions, setshowActions] = useState(-1);
  const [selectedRowIndex, setSelectedRowIndex] = useState(rowIndexSelected);
  const [selectedRowData, setSelectedRowData] = useState<{ [key: string]: any }>({});
  const [visibleColumns, setVisibleColumns] = useState<string[]>();
  const [operation, setOperation] = useState<string>();
  const [colorArray, setColorArray] = useState({ colorArray: [], borderChanges: [], subgroupIndexChanges: [] });
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  const [subGroupColumnIndex] = useState(
    tableColumns?.findIndex((column) => column.accessor === ColorOptions.subgroupColumnName),
  );

  useEffect(() => {
    if (clearTheSelection) {
      setSelectedRowIndex(-1);
      setSelectedRowData({});
    }
  }, [clearTheSelection]);

  useEffect(() => {
    //console.log("tableDataChanged");
    if (tableData && tableData.length > 0) {
      setEditableData(tableData);
      setIsEditing(false);
      setIsInserting(false);
      setEditedRowIndex(-1);
    }

    setVisibleColumns(
      tableColumns?.filter((column) => !hiddenColumns?.includes(column.accessor))?.map((column) => column.accessor),
    );
    //console.log(ColorOptions);
    if (ColorOptions.useTheColorOptions) {
      assignColorsToData(tableData, ColorOptions);
    }
  }, [tableData]);

  const handleExpandCell = (event_loc: SyntheticEvent, index_loc: number) => {
    event_loc.preventDefault();
    event_loc.stopPropagation();
    setExpandedRowIndex(index_loc);
  };

  const assignColorsToData = (dataToLookThrough: any[], ColorGenerationOptions: EditableTableProps['ColorOptions']) => {
    const colorArray = generateColors(dataToLookThrough, ColorGenerationOptions);
    setColorArray(colorArray);
  };

  const handleColumnVisibilityChange = (selectedColumns: string[]) => {
    ////console.log(selectedColumns);
    setVisibleColumns(selectedColumns);
  };

  const renderColumnVisibilityDropdown = () => {
    return (
      <Select
        mode="multiple"
        value={visibleColumns}
        onChange={handleColumnVisibilityChange}
        style={{ minWidth: '40vw' }}
      >
        {tableColumns?.map((columnName) => (
          <Select.Option key={columnName.accessor} value={columnName.accessor}>
            {columnName.Header}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const handleRowClicked = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, index: number, columnName: string) => {
      //console.log("handling row selection");
      event.preventDefault();
      event.stopPropagation(); // Prevent triggering the row click when clicking buttons
      if (allowSelection) {
        if (index === selectedRowIndex || isEditing) {
          setSelectedRowIndex(-1);
        } else if (!isEditing) {
          setSelectedRowIndex(index);
        }
        setSelectedRowData(editableData[index]);
        handleSelection(index, editableData[index], event, columnName);
      }
    },
    [allowSelection, editableData, handleSelection, isEditing, selectedRowIndex],
  );

  const handleEditClick = (index: number) => {
    setVisibleColumns(tableColumns?.map((item) => item.accessor)?.filter((item) => !stillHiddenColumns.includes(item)));
    setOperation('edit');
    setIsEditing(true);
    setEditedRowIndex(index);
    setIsInserting(false); // Reset insert state
  };

  const handleSaveClick = () => {
    let validationSuccess = true;
    // Update the current data in the table
    const updatedData = [...editableData];
    ////console.log(editedRowIndex);
    if (editedRowIndex !== -1 && !isInserting) {
      // Editing an existing row
      updatedData[editedRowIndex] = { ...editableData[editedRowIndex] };
      // For example, you can trim whitespace from all string values
      Object.keys(updatedData[editedRowIndex]).forEach((key) => {
        if (
          mandatoryFields.includes(key) &&
          (updatedData[editedRowIndex][key] === '' ||
            updatedData[editedRowIndex][key] === null ||
            updatedData[editedRowIndex][key] === undefined)
        ) {
          Notification.error(
            'Error',
            `${key.toUpperCase()} field is mandatory, data not saved`,
            undefined,
            'medium',
            1,
          );
          validationSuccess = false;
        }

        if (typeof updatedData[editedRowIndex][key] === 'string') {
          updatedData[editedRowIndex][key] = updatedData[editedRowIndex][key].trim();
        }
      });
    } else {
      // Inserting a new row
      const newRowIndex = isInserting ? editedRowIndex : updatedData.length - 1; // Index of the newly inserted row
      updatedData[newRowIndex] = { ...editableData[newRowIndex] };
      // For example, you can trim whitespace from all string values

      Object.keys(updatedData[newRowIndex]).forEach((key) => {
        ////console.log(key, updatedData[newRowIndex][key]);

        if (
          mandatoryFields.includes(key) &&
          (updatedData[newRowIndex][key] === '' ||
            updatedData[newRowIndex][key] === null ||
            updatedData[newRowIndex][key] === undefined)
        ) {
          Notification.error(
            'Error',
            `${key.toUpperCase()} field is mandatory, data not saved`,
            undefined,
            'medium',
            1,
          );
          validationSuccess = false;
        }

        if (typeof updatedData[newRowIndex][key] === 'string') {
          updatedData[newRowIndex][key] = updatedData[newRowIndex][key].trim();
        }
      });
    }
    // Call handleSave with the updated data
    if (saveReturnType === 'complete' && validationSuccess) {
      handleSave(updatedData, operation);
    } else if (saveReturnType === 'row' && validationSuccess) {
      handleSave(updatedData[editedRowIndex], operation, editedRowIndex);
    }

    //if validation was not success, don't reset states.
    if (validationSuccess) {
      setIsEditing(false);
      setEditedRowIndex(-1);
      setIsInserting(false); // Reset insert state
    }

    setVisibleColumns(
      tableColumns.filter((column) => !hiddenColumns.includes(column.accessor)).map((column) => column.accessor),
    );
  };

  const handleCancelClick = (index: number) => {
    //special case in inserting mode,
    //if insert was not completed then the row continues to stay half completed.
    //in such case remove it.
    if (isInserting) {
      const updatedData = [...editableData];
      updatedData.splice(index, 1)[0]; // Remove the row and save it
      setEditableData(updatedData);
    }
    setVisibleColumns(
      tableColumns.filter((column) => !hiddenColumns.includes(column.accessor)).map((column) => column.accessor),
    );
    setIsEditing(false);
    setEditedRowIndex(-1);
    setIsInserting(false); // Reset insert state
  };

  const handleDeleteRow = (index: number) => {
    setVisibleColumns(
      tableColumns.filter((column) => !hiddenColumns.includes(column.accessor)).map((column) => column.accessor),
    );
    setOperation('delete');
    const updatedData = [...editableData];
    const deletedRow = updatedData.splice(index, 1)[0]; // Remove the row and save it
    setEditableData(updatedData);
    handleSave(saveReturnType === 'row' ? deletedRow : updatedData, 'delete', index);
  };

  const handleMoveRow = (index: number, direction: 'up' | 'down') => {
    setOperation(`move_${direction}`);
    let updatedData: any = [];
    if (direction === 'up' && index > 0) {
      updatedData = [...editableData];
      [updatedData[index - 1], updatedData[index]] = [updatedData[index], updatedData[index - 1]];
      setEditableData(updatedData);
    } else if (direction === 'down' && index < editableData.length - 1) {
      updatedData = [...editableData];
      [updatedData[index], updatedData[index + 1]] = [updatedData[index + 1], updatedData[index]];
      setEditableData(updatedData);
    }
    handleSave(updatedData, `move_${direction}`);
  };

  const handleInsertNew = (index: number, direction: 'up' | 'down' | 'at') => {
    const currentSelection = editableData[index];
    setVisibleColumns(tableColumns.map((item) => item.accessor).filter((item) => !stillHiddenColumns.includes(item)));
    let newRowIndex;
    setOperation(`insert_${direction}`);
    if (direction === 'up') {
      newRowIndex = index;
    } else if (direction === 'down') {
      newRowIndex = index + 1;
    } else if (direction === 'at') {
      newRowIndex = index; // Insert at the specified index
    } else {
      // Handle invalid direction
      console.error('Invalid direction:', direction);
      return;
    }

    /*  console.log(tableColumns); */
    const newBlankRow = Object.fromEntries(
      (tableColumns || []).map((column) => [
        column.accessor,
        defaultValuesColumns && defaultValuesColumns.includes(column.accessor) ? currentSelection[column.accessor] : '',
      ]),
    );

    const updatedData = [...editableData];
    updatedData.splice(newRowIndex, 0, newBlankRow);

    setIsEditing(true);
    setEditedRowIndex(newRowIndex);
    setIsInserting(true); // Set insert state
    setEditableData(updatedData);
    setshowActions(newRowIndex);
  };

  const renderExpandableCells = useCallback(
    (column: any, rowIndex: number, rowData: any) => {
      return (
        <React.Fragment key={`${column.accessor}-expandable`}>
          <div
            className={`expandable-cell ${expandedRowIndex === rowIndex ? 'expanded' : ''}`}
            onClick={(event) => handleExpandCell(event, expandedRowIndex === rowIndex ? -1 : rowIndex)}
            // onMouseEnter={(event) => handleExpandCell(event, rowIndex)}
            // onMouseLeave={(event) => handleExpandCell(event, -1)}
          >
            <div className="expandable-preview">
              <Button
                className="expandable-preview-button"
                key="insertUpButton"
                type="tertiary"
                size="small"
                prefixIcon={{
                  name: expandedRowIndex === rowIndex ? 'keyboard_double_arrow_up' : 'keyboard_double_arrow_down',
                }}
              />
              <div className="expandable-preview-data">{rowData[column.accessor]?.length} items</div>
            </div>
          </div>
          {expandedRowIndex === rowIndex && !isInserting && (
            <div className="expanded-content-cell" key={`${column.accessor}-expanded-content`}>
              <div className="expanded-content">
                <div className="expanded-content-heading">
                  {rowData[column.accessor]?.length} {column.Header}
                </div>
                <div className="expanded-content-cell-list">
                  {rowData[column.accessor].map((item: any, itemIndex: number) => (
                    <div key={item} className="expanded-content-cell-list-item">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </React.Fragment>
      );
    },
    [expandedRowIndex, isInserting],
  );

  const renderEditCells = useCallback(
    (column: any, rowData: any) => {
      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, columnName: string) => {
        const updatedData = [...editableData];
        const columnEditType = tableColumns.filter((oneColumn) => {
          if (oneColumn.accessor === columnName) {
            return oneColumn;
          }
        });

        ////console.log(columnEditType[0].editType);

        if (columnEditType[0].editType === 'Boolean') {
          updatedData[editedRowIndex][columnName] = e.target.checked;
          ////console.log('checked');
        } else {
          updatedData[editedRowIndex][columnName] = e.target.value;
          ////console.log('value');
        }
        ////console.log(updatedData[editedRowIndex][columnName]);
        setEditableData(updatedData);
      };

      const handleSelectChange = (value: string, columnName: string) => {
        const updatedData = [...editableData];
        updatedData[editedRowIndex][columnName] = value;
        setEditableData(updatedData);
      };

      if (column.editType === 'Number') {
        return (
          <Input
            type="number"
            value={rowData[column.accessor]}
            onChange={(e) => handleInputChange(e, column.accessor)}
          />
        );
      } else if (column.editType === 'inputField') {
        return (
          <Input
            type="text"
            value={rowData[column.accessor]}
            onChange={(e) => handleInputChange(e, column.accessor)}
            style={{ maxWidth: '250px' }}
          />
        );
      } else if (column.editType === 'Boolean') {
        return (
          <Input
            type="checkbox"
            checked={rowData[column.accessor]}
            onChange={(e) => handleInputChange(e, column.accessor)}
            style={{ maxWidth: '60px' }}
          />
        );
      } else if (column.editType === 'Combobox') {
        return (
          <Select
            value={rowData[column.accessor]}
            onChange={(value) => handleSelectChange(value, column.accessor)}
            style={{ minWidth: '100px' }}
            dropdownStyle={{ minWidth: '300px' }}
          >
            {column.options?.map((options) => (
              <Option key={options} value={selectInputReturnType === 'value' ? options.value : options.display}>
                {options.display}
              </Option>
            ))}
          </Select>
        );
      } else if (column.editType === 'Boolean') {
        return <Input type="checkbox" checked={rowData[column.accessor]} style={{ maxWidth: '60px' }} />;
      }
    },
    [editableData, editedRowIndex, selectInputReturnType, tableColumns],
  );

  const renderCell = useCallback(
    (column: any, rowData: any, rowIndex: number) => {
      // Handling expandable columns
      if (expandableColumns.includes(column.accessor)) {
        return renderExpandableCells(column, rowIndex, rowData);
      }

      // Handling editable cells
      if (isEditing && !isInserting && editedRowIndex === rowIndex && !onlyAddableFields.includes(column.accessor)) {
        return renderEditCells(column, rowData);
      }

      if (isEditing && isInserting && editedRowIndex === rowIndex) {
        return renderEditCells(column, rowData);
      }

      // Handling non-editable cells
      const shouldApplyLinkDecoration =
        column.linkType &&
        (!column?.linkExceptions ||
          (column?.linkExceptions && !column?.linkExceptions.includes(rowData[column.accessor])));

      if (column.editType === 'Boolean') {
        return (
          <styled.ConditionalStyledDiv shouldApplyLinkDecoration={shouldApplyLinkDecoration}>
            <Switch size="small" checked={rowData[column.accessor]} />
          </styled.ConditionalStyledDiv>
        );
      }
      return (
        <styled.ConditionalStyledDiv shouldApplyLinkDecoration={shouldApplyLinkDecoration}>
          {rowData[column.accessor] || ''}
        </styled.ConditionalStyledDiv>
      );
    },
    [
      expandableColumns,
      isEditing,
      editedRowIndex,
      onlyAddableFields,
      isInserting,
      renderExpandableCells,
      renderEditCells,
    ],
  );

  const handleHamburgerClicked = () => {
    sethamburgerOpen(!hamBurgerOpen);
  };

  const message = isEditing || isInserting ? '* Columns are mandatory' : 'Select Columns';

  const rowHamburgerToggle = (index: number) => {};

  const renderOnlyEditIcon = (index: number) => {
    const afterEditAction = () => (
      <div style={{ display: 'flex' }}>
        <Tooltip title="Save Changes" mouseEnterDelay={1}>
          <Button
            key="saveButton"
            type="tertiary"
            size="small"
            onClick={() => handleSaveClick()}
            prefixIcon={{ name: 'save' }}
          />
        </Tooltip>
        <Tooltip title="Cancel" mouseEnterDelay={1}>
          <Button
            key="cancelButton"
            type="tertiary"
            size="small"
            onClick={() => handleCancelClick(index)}
            prefixIcon={{ name: 'cancel' }}
          />
        </Tooltip>
      </div>
    );
    return isEditing && editedRowIndex === index && showActions_ext
      ? afterEditAction()
      : showActions_ext && (
          <Tooltip title="Edit" mouseEnterDelay={1}>
            <Button
              key="editButton"
              type="tertiary"
              size="small"
              onClick={() => handleEditClick(index)}
              prefixIcon={{ name: 'edit' }}
            />
          </Tooltip>
        );
  };

  const renderOnlyDeleteIcon = (index: number) => {
    return (
      ButtonsVisible.Delete && (
        <PopConfirm
          title={<div> Are you sure you want to delete? </div>}
          onConfirm={() => handleDeleteRow(index)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Tooltip title="Delete" mouseEnterDelay={1}>
            <Button key="deleteButton" type="tertiary" size="small" prefixIcon={{ name: 'delete' }} />
          </Tooltip>
        </PopConfirm>
      )
    );
  };

  const renderActionItems = (index: number) => {
    // Check if at least one button is set to visible
    const anyButtonVisible =
      ButtonsVisible.Edit ||
      ButtonsVisible.MoveUp ||
      ButtonsVisible.MoveDown ||
      ButtonsVisible.InsertDown ||
      ButtonsVisible.Delete;

    const isCurrentRowEditing = isEditing && editedRowIndex === index;

    // If no buttons are visible and the row isn't currently being edited, render nothing.
    if (!anyButtonVisible && !isCurrentRowEditing) {
      return null;
    }

    const afterEditAction = () => (
      <div className="buttons-div-edit">
        <div className="buttons-div-button">
          <Tooltip title="Save Changes" mouseEnterDelay={1}>
            <Button
              key="saveButton"
              type="tertiary"
              size="small"
              onClick={() => handleSaveClick()}
              prefixIcon={{ name: 'save' }}
            />
          </Tooltip>
        </div>
        <div className="buttons-div-button">
          <Tooltip title="Cancel" mouseEnterDelay={1}>
            <Button
              key="cancelButton"
              type="tertiary"
              size="small"
              onClick={() => handleCancelClick(index)}
              prefixIcon={{ name: 'cancel' }}
            />
          </Tooltip>
        </div>
      </div>
    );

    const theActionItems =
      isCurrentRowEditing && showActions_ext
        ? afterEditAction()
        : showActions_ext && (
            <div className="buttons-div">
              <div className="buttons-div-button">
                {ButtonsVisible.Edit && (
                  <Tooltip title="Edit" mouseEnterDelay={1}>
                    <Button
                      key="editButton"
                      type="tertiary"
                      size="small"
                      onClick={() => handleEditClick(index)}
                      prefixIcon={{ name: 'edit' }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="buttons-div-button">
                {ButtonsVisible.MoveUp && (
                  <Tooltip title="Move Row Up" mouseEnterDelay={1}>
                    <Button
                      key="moveUPButton"
                      type="tertiary"
                      size="small"
                      onClick={() => handleMoveRow(index, 'up')}
                      prefixIcon={{ name: 'move_up' }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="buttons-div-button">
                {/* Note: You might want to check if this should be ButtonsVisible.InsertUp instead of MoveDown */}
                {ButtonsVisible.MoveDown && (
                  <Tooltip title="Insert Above" mouseEnterDelay={1}>
                    <Button
                      key="insertUpButton"
                      type="tertiary"
                      size="small"
                      onClick={() => handleInsertNew(index, 'up')}
                      prefixIcon={{ name: 'arrow_upward' }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="buttons-div-button">
                {ButtonsVisible.InsertDown && (
                  <Tooltip title="Insert Below" mouseEnterDelay={1}>
                    <Button
                      key="insertDownButton"
                      type="tertiary"
                      size="small"
                      onClick={() => handleInsertNew(index, 'down')}
                      prefixIcon={{ name: 'arrow_downward' }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="buttons-div-button">
                {ButtonsVisible.MoveDown && (
                  <Tooltip title="Move Row Down" mouseEnterDelay={1}>
                    <Button
                      key="moveDownButton"
                      type="tertiary"
                      size="small"
                      onClick={() => handleMoveRow(index, 'down')}
                      prefixIcon={{ name: 'move_down' }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="buttons-div-button">
                {ButtonsVisible.Delete && (
                  <PopConfirm
                    title={<div>Are you sure you want to delete?</div>}
                    onConfirm={() => handleDeleteRow(index)}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Tooltip title="Delete" mouseEnterDelay={1}>
                      <Button key="deleteButton" type="tertiary" size="small" prefixIcon={{ name: 'delete' }} />
                    </Tooltip>
                  </PopConfirm>
                )}
              </div>
            </div>
          );

    return (
      <Hamburger
        dropdownContent={theActionItems}
        isOpen={isCurrentRowEditing && showActions_ext}
        handleClick={() => rowHamburgerToggle(index)}
        displayText={''}
      />
    );
  };

  return (
    <ThemeProvider themes={themes} selectedMode={themeMode}>
      <div className="editable-table">
        <div className="messageSection">
          {!isEditing ? (
            <div style={{ display: 'flex', marginLeft: '2px' }}>
              {allowInsertInitialBlankRow && tableData && tableData.length <= 0 && (
                <Tooltip title="Insert Above" mouseEnterDelay={1}>
                  <Button
                    key="insertUpButton"
                    type="primary"
                    size="small"
                    onClick={() => handleInsertNew(0, 'at')}
                    prefixIcon={{ name: 'add_box' }}
                  />
                </Tooltip>
              )}
              {searchBarOptions.showSearch && (
                <Button
                  key="saveButton"
                  type="ghost"
                  size="small"
                  onClick={() => searchBarOptions.toggleSearch(searchBarOptions.visible)}
                  style={{
                    backgroundColor: !searchBarOptions.visible ? '' : '#0775bc',
                  }}
                  prefixIcon={{ name: 'search', style: { color: !searchBarOptions.visible ? '' : '#ffffff' } }}
                />
              )}

              <Hamburger
                dropdownContent={renderColumnVisibilityDropdown()}
                isOpen={hamBurgerOpen}
                handleClick={() => handleHamburgerClicked()}
                displayText={<styled.TableHeaderDiv>{Table_Header}</styled.TableHeaderDiv>}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100vw' }}>
              <div className="messageSectionMessage">{message}</div>
            </div>
          )}
        </div>
        {tableData && tableData.length <= 0 && loadingSpinnerVisible ? (
          <div style={{ justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <LoadingComponent />
          </div>
        ) : (
          <styled.CustomTable className="custom-table" key="mainTable">
            <styled.TableHeaderHead key="mainTableHeader">
              <styled.TableHeader className="table-header">
                {showSerialNumbers && (
                  <styled.TableHeaderCell>
                    <div className="table-header-cell-text">S. No.</div>
                  </styled.TableHeaderCell>
                )}
                {tableColumns?.map(
                  (column, index) =>
                    visibleColumns?.includes(column.accessor) && (
                      <styled.TableHeaderCell key={index}>
                        {!searchBarOptions.visible ? (
                          <div className="table-header-cell-text">
                            {column.Header}
                            {(isEditing || isInserting) && mandatoryFields?.includes(column.accessor) ? '*' : ''}
                          </div>
                        ) : (
                          <div>
                            <SearchInput
                              key={index}
                              placeholder={column.accessor}
                              style={{ width: '100%' }}
                              onChange={(searchTerm) =>
                                searchBarOptions.handleSearch(searchTerm.target.value, column.accessor)
                              }
                            />
                          </div>
                        )}
                      </styled.TableHeaderCell>
                    ),
                )}
                {showActions_ext && (
                  <styled.TableHeaderCell>
                    <div className="table-header-cell-text" />
                  </styled.TableHeaderCell>
                )}
              </styled.TableHeader>
            </styled.TableHeaderHead>
            <tbody key="mainTableBody" className="table-body-body">
              {editableData?.map((rowData, index) => {
                const colorValue = colorArray.mainGroupColorArray?.find(
                  (element) => element.field === rowData[ColorOptions.mainGroupColumnName],
                );
                const borderValue = colorArray.mainGroupIndexChanges?.includes(index + 1)
                  ? '2px solid grey'
                  : colorArray.subgroupIndexChanges?.includes(index + 1)
                    ? '1px dashed grey'
                    : '1px solid #dddddd';
                const subColorValue = colorArray.subGroupColorArray?.find(
                  (element) => element.field === rowData[ColorOptions.subgroupColumnName],
                );

                return (
                  <styled.TableRow
                    key={index}
                    isLight={index % 2 === 0}
                    isSelected={index === selectedRowIndex}
                    style={{ borderBottom: ColorOptions.thickBorders ? borderValue : '' }}
                  >
                    {showSerialNumbers && (
                      <styled.TableDataCell style={{ backgroundColor: groupByColours ? colorValue?.color : '' }}>
                        {selectedRowIndex === index && showLoadingOnSelection ? (
                          <div>
                            <LoadingComponentScalable borderWidth={2} containerSize={30} />
                          </div>
                        ) : (
                          <div>{index + 1}</div>
                        )}
                      </styled.TableDataCell>
                    )}
                    {tableColumns.map(
                      (column, columnIndex) =>
                        visibleColumns?.includes(column.accessor) && (
                          <styled.TableDataCell
                            key={columnIndex}
                            onClick={(event) => {
                              if (!isEditing && !isInserting) handleRowClicked(event, index, column);
                            }}
                            style={{
                              backgroundColor: groupByColours
                                ? columnIndex >= subGroupColumnIndex
                                  ? subColorValue?.color
                                  : colorValue?.color
                                : '',
                            }}
                          >
                            {renderCell(column, rowData, index)}
                          </styled.TableDataCell>
                        ),
                    )}
                    {showActions_ext && (
                      <styled.ActionsColumn style={{ backgroundColor: groupByColours ? subColorValue?.color : '' }}>
                        {showOnlyEditIcon
                          ? renderOnlyEditIcon(index)
                          : showOnlyDeleteIcon
                            ? renderOnlyDeleteIcon(index)
                            : renderActionItems(index)}
                      </styled.ActionsColumn>
                    )}
                  </styled.TableRow>
                );
              })}
            </tbody>
          </styled.CustomTable>
        )}
      </div>
    </ThemeProvider>
  );
};

export default EditableTable;
